import { computed, signal, DestroyRef } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime } from 'rxjs';
import type { PageEvent } from '@angular/material/paginator';
import type { Document, DocumentType } from '../model';
import { CompanyService } from '../common/service/company.service';
import { DocumentTypeService } from '../common/service/document-type.service';
import { DocumentService, PreviewDocument } from '../common/service/document.service';
import { formatJsonDate } from '../util';
import type {
  DocumentLinkDialogData,
  DocumentCategory,
  CategoryGroup,
  FilterState,
  SortField,
} from './document-link.model';

export interface DocumentLinkListContext {
  onDocumentsLoaded: () => void;
  loadDocuments: (page: number, size: number) => Promise<void>;
}

/** Catalog loading, previews, filters, pagination and sorting for the linking dialog. */
export class DocumentLinkListState {
  readonly enforcedCategory = signal<DocumentCategory | null>(null);
  readonly filters = this.fb.nonNullable.group({
    search: [''],
    typeId: [''],
    start: [null as Date | null],
    end: [null as Date | null],
  });

  readonly documents = signal<Document[]>([]);
  readonly previewMap = signal<Record<string, PreviewDocument>>({});
  readonly total = signal(0);
  readonly pageIndex = signal(0);
  readonly pageSize = signal(20);
  readonly sortField = signal<SortField>('date');
  readonly sortDirection = signal<'asc' | 'desc'>('desc');
  readonly filterState = signal<FilterState>(this.filters.getRawValue());
  readonly documentTypes = signal<DocumentType[]>([]);
  readonly typesLoading = signal(true);
  readonly typeSearchTerm = signal('');
  readonly typeSearchControl = this.fb.control('');
  readonly typeSearchActive = computed(() => !!this.typeSearchTerm());
  readonly typeGroups = computed(() =>
    this.buildCategoryGroups(this.filterTypes(this.documentTypes(), this.typeSearchTerm()))
  );
  readonly allowedCategories = signal<DocumentCategory[] | null>(null);
  readonly filtersApplied = computed(() => {
    const filters = this.filterState();
    const enforced = this.enforcedCategory();
    const hasSearch = !!filters.search?.trim();
    let hasType = !!filters.typeId;
    if (enforced) {
      hasType = filters.typeId !== this.categoryFilterValue(enforced);
    }
    const hasDate = !!filters.start || !!filters.end;
    return hasSearch || hasType || hasDate;
  });
  constructor(
    private readonly data: DocumentLinkDialogData,
    private readonly fb: FormBuilder,
    private readonly companyService: CompanyService,
    private readonly documentTypeService: DocumentTypeService,
    private readonly documentService: DocumentService,
    private readonly context: DocumentLinkListContext
  ) {}

  bindTypeSearch(destroyRef: DestroyRef): void {
    this.typeSearchControl.valueChanges
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe((value) => this.typeSearchTerm.set((value ?? '').toLowerCase().trim()));
  }
  initializeFilters(destroyRef: DestroyRef): void {
    const initialCategory = this.data.category ?? null;
    if (initialCategory) {
      this.enforcedCategory.set(initialCategory);
      this.allowedCategories.set([initialCategory]);
    }
    this.resetFiltersToDefault(false);
    this.filters.valueChanges.pipe(debounceTime(250), takeUntilDestroyed(destroyRef)).subscribe((value) => {
      this.filterState.set({
        search: value.search ?? '',
        typeId: value.typeId ?? '',
        start: value.start ?? null,
        end: value.end ?? null,
      });
      void this.context.loadDocuments(0, this.pageSize());
    });
  }
  async loadDocumentTypes() {
    this.typesLoading.set(true);
    try {
      const category = this.enforcedCategory();
      const types = await this.documentTypeService.listAll(category ?? undefined);
      this.documentTypes.set(types ?? []);
    } catch (error) {
      console.error('Failed to load document types', error);
      this.documentTypes.set([]);
    } finally {
      this.typesLoading.set(false);
    }
  }

  async loadDocuments(page = this.pageIndex(), size = this.pageSize()) {
    const companyId = this.data.company.id;
    if (!companyId) {
      this.documents.set([]);
      this.previewMap.set({});
      this.total.set(0);
      return;
    }

    const query: Record<string, string | number | null | undefined> = {
      page,
      size,
      sort: `${this.sortField()},${this.sortDirection()}`,
    };
    const { search, typeId, start, end } = this.filterState();
    const categoryFilter = this.extractCategoryFilter(typeId);
    const trimmedSearch = search?.trim();
    if (trimmedSearch) {
      query['search'] = trimmedSearch;
    }
    if (categoryFilter) {
      query['documentTypeCategory'] = categoryFilter;
    } else if (typeId) {
      query['documentType'] = typeId;
    } else if (this.enforcedCategory()) {
      query['documentTypeCategory'] = this.enforcedCategory();
    }
    if (start) {
      query['dateStart'] = formatJsonDate(start);
    }
    if (end) {
      query['dateEnd'] = formatJsonDate(end);
    }

    try {
      const pageData = await this.companyService.allDocumentsForCompanyTeam(companyId, query);
      const docs = pageData?.content ?? [];
      this.documents.set(docs);
      this.total.set(pageData?.totalElements ?? docs.length);
      this.pageIndex.set(pageData?.pageNumber ?? page);
      this.pageSize.set(pageData?.pageSize ?? size);
      await this.loadPreviews(docs);
      this.context.onDocumentsLoaded();
    } catch (error) {
      console.error('Failed to load company documents', error);
      this.documents.set([]);
      this.total.set(0);
      this.previewMap.set({});
    }
  }

  async loadPreviews(documents: Document[]) {
    const ids = documents.map((doc) => doc.id).filter((id): id is string => Boolean(id));
    if (!ids.length) {
      this.previewMap.set({});
      return;
    }
    try {
      this.previewMap.set(await this.documentService.previewList(ids));
    } catch (error) {
      console.warn('Failed to load document previews', error);
      this.previewMap.set({});
    }
  }

  handlePageChange(event: PageEvent) {
    this.pageIndex.set(event.pageIndex);
    this.pageSize.set(event.pageSize);
    void this.context.loadDocuments(event.pageIndex, event.pageSize);
  }

  changeSort(field: SortField) {
    if (this.sortField() === field) {
      this.sortDirection.set(this.sortDirection() === 'asc' ? 'desc' : 'asc');
    } else {
      this.sortField.set(field);
      this.sortDirection.set(field === 'title' ? 'asc' : 'desc');
    }
    void this.context.loadDocuments(0, this.pageSize());
  }

  categoryFilterValue(category: DocumentCategory) {
    return `category:${category}`;
  }

  buildCategoryGroups(types: DocumentType[]): CategoryGroup[] {
    const grouped = new Map<DocumentCategory, DocumentType[]>();
    const allowed = this.allowedCategories();
    types.forEach((type) => {
      const category = (type.category ?? 'company') as DocumentCategory;
      if (allowed && !allowed.includes(category)) {
        return;
      }
      const bucket = grouped.get(category);
      if (bucket) {
        bucket.push(type);
      } else {
        grouped.set(category, [type]);
      }
    });
    return Array.from(grouped.entries())
      .map(([category, list]) => ({
        category,
        labelKey: this.categoryLabelKey(category),
        types: [...list].sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => a.category.localeCompare(b.category));
  }

  categoryLabelKey(category: DocumentCategory) {
    switch (category) {
      case 'chain':
        return 'chain_tr';
      case 'report':
        return 'Reports_tr';
      case 'attributes':
        return 'product_attributes_tr';
      default:
        return 'Company_tr';
    }
  }

  filterTypes(types: DocumentType[], term: string) {
    const normalized = term.trim().toLowerCase();
    if (!normalized) {
      return types;
    }
    return types.filter((type) => type.name.toLowerCase().includes(normalized));
  }

  extractCategoryFilter(value: string | null | undefined): DocumentCategory | null {
    if (!value?.startsWith('category:')) {
      return null;
    }
    const suffix = value.slice('category:'.length) as DocumentCategory;
    if (['company', 'chain', 'report', 'attributes'].includes(suffix)) {
      return suffix as DocumentCategory;
    }
    return null;
  }

  resetFiltersToDefault(triggerLoad: boolean) {
    const defaults = this.buildDefaultFilterState();
    this.filters.reset(defaults, { emitEvent: false });
    this.filterState.set(defaults);
    if (triggerLoad) {
      void this.context.loadDocuments(0, this.pageSize());
    }
  }

  buildDefaultFilterState(): FilterState {
    const enforced = this.enforcedCategory();
    return {
      search: '',
      typeId: enforced ? this.categoryFilterValue(enforced) : '',
      start: null,
      end: null,
    };
  }
}
